// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { FloatingLabelInput, FloatingLabelSelect } from "@aviary";
import { e2eAttributes } from "@shared/e2eAttributes";
import { l } from "@shared/locales/i18n";
import type {
  BankInformationErrors,
  BankAccountInfoFieldValues,
} from "@shared/types/fields/bankInformationTypes";

import { AccountNumberField } from "../AccountNumberField/AccountNumberField";
import { RoutingNumberField } from "../RoutingNumberField/RoutingNumberField";

interface Props {
  values: BankAccountInfoFieldValues;
  errors: BankInformationErrors["fields"];
  onChange: (values: BankAccountInfoFieldValues) => void;
}

const BankAccountInfoFields = ({ values, errors, onChange }: Props) => {
  const { t } = useTranslation();

  const handleOnChange = e => {
    onChange({
      ...values,
      [e.target.id]: e.target.value,
    });
  };

  const routingAndAccountNumFields = () => {
    return (
      <>
        <Columns.Column columnWidth={6}>
          <RoutingNumberField
            value={values?.routingNumber ?? ""}
            errors={errors?.routingNumber}
            onChange={handleOnChange}
          />
        </Columns.Column>

        <Columns.Column columnWidth={6}>
          <AccountNumberField
            value={values?.accountNumber}
            errors={errors?.accountNumber}
            onChange={handleOnChange}
          />
        </Columns.Column>
      </>
    );
  };

  const accountNameAndHeldByFields = () => {
    return (
      <>
        <Columns.Column columnWidth={6} tabletWidth={12}>
          <FloatingLabelInput
            data-testid="account-holder-input"
            type="text"
            id="accountHolder"
            value={values?.accountHolder ?? ""}
            label={t(l.sharedOnboarding.bankingInfo.accountHolderName)}
            required
            maxLength={50}
            handleChange={handleOnChange}
            errors={errors?.accountHolder}
            data-e2e={e2eAttributes.paymentSettings.accountHolder}
          />
        </Columns.Column>

        <Columns.Column columnWidth={6} tabletWidth={12}>
          <FloatingLabelSelect
            data-testid="account-held-by-input"
            type="text"
            id="accountHeldBy"
            label={t(l.sharedOnboarding.bankingInfo.accountHeldBy)}
            value={values?.accountHeldBy ?? ""}
            required
            handleChange={handleOnChange}
            errors={errors?.accountHeldBy}
          >
            <option value="individual">{t(l.sharedOnboarding.bankingInfo.individual)}</option>
            <option value="company">{t(l.sharedOnboarding.bankingInfo.company)}</option>
          </FloatingLabelSelect>
        </Columns.Column>
      </>
    );
  };

  return (
    <Columns>
      {accountNameAndHeldByFields()}
      {routingAndAccountNumFields()}
      <Columns.Column columnWidth={6} tabletWidth={12}>
        <FloatingLabelSelect
          type="text"
          id="accountType"
          value={values?.accountType ?? ""}
          label={t(l.sharedOnboarding.bankingInfo.accountType)}
          required
          handleChange={handleOnChange}
          errors={errors?.accountType}
        >
          <option value="checking">{t(l.sharedOnboarding.bankingInfo.checking)}</option>
          <option value="savings">{t(l.sharedOnboarding.bankingInfo.savings)}</option>
        </FloatingLabelSelect>
      </Columns.Column>
    </Columns>
  );
};

export { BankAccountInfoFields };
export type { BankAccountInfoFieldValues };
